import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronRight, ArrowDown } from "lucide-react";
import { IMAGES, TAGLINES } from "../../lib/data";
import { SkewButton } from "../ui-kit";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.4 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

function MaskLine({ children, i }) {
  return (
    <span className="block overflow-hidden">
      <motion.span variants={lineVariants} custom={i} initial="hidden" animate="show" className="block">
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [tagIndex, setTagIndex] = useState(0);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const t = setInterval(() => setTagIndex((i) => (i + 1) % TAGLINES.length), 3200);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const x = (e.clientX / window.innerWidth - 0.5) * 2;
    const y = (e.clientY / window.innerHeight - 0.5) * 2;
    setMouse({ x, y });
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      className="relative min-h-screen flex items-center overflow-hidden noise"
      data-testid="hero-section"
    >
      {/* Background image + parallax */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
      </motion.div>

      {/* Grid + glow */}
      <div className="absolute inset-0 z-0 grid-lines opacity-40" />
      <div
        className="absolute z-0 h-[600px] w-[600px] rounded-full blur-3xl"
        style={{
          background: "radial-gradient(circle, rgba(255,90,0,0.22) 0%, transparent 70%)",
          right: "-5%",
          top: "20%",
          transform: `translate(${mouse.x * -20}px, ${mouse.y * -20}px)`,
        }}
      />

      {/* Floating particles */}
      {Array.from({ length: 18 }).map((_, i) => (
        <motion.span
          key={i}
          className="absolute z-10 rounded-full bg-orange-500/60"
          style={{
            width: 2 + (i % 3),
            height: 2 + (i % 3),
            left: `${(i * 53) % 100}%`,
            top: `${(i * 37) % 100}%`,
          }}
          animate={{ y: [0, -30, 0], opacity: [0, 0.9, 0] }}
          transition={{ duration: 4 + (i % 5), repeat: Infinity, delay: i * 0.3, ease: "easeInOut" }}
        />
      ))}

      {/* Product render (branded) */}
      <div
        className="hidden lg:block absolute z-10 right-0 xl:right-6 top-1/2 w-[46%] max-w-2xl pointer-events-none"
        style={{ transform: `translateY(-50%) translate(${mouse.x * 18}px, ${mouse.y * 12}px)`, transition: "transform 0.2s ease-out" }}
      >
        <motion.img
          src={IMAGES.tools}
          alt="Sobtrex professional power tools"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: [40, 0, -12, 0] }}
          transition={{ opacity: { duration: 1, delay: 0.6 }, y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.6 } }}
          className="w-full h-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.85)]"
        />
      </div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-20 mx-auto max-w-7xl px-5 md:px-8 w-full pt-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-10 bg-orange-500" />
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-orange-500">
            Coming Soon — India
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.92] tracking-tighter text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskLine i={0}>A New</MaskLine>
          <MaskLine i={1}>
            <span className="text-glow-orange text-[#ff5a00]">Generation</span>
          </MaskLine>
          <MaskLine i={2}>of Power Tools</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-base md:text-lg text-zinc-300 leading-relaxed"
        >
          Sobtrex is preparing to launch a premium range of professional corded and cordless
          power tools engineered for reliability, performance, and value — built for professionals,
          workshops, contractors and industrial users across India.
        </motion.p>

        {/* Rotating tagline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-4 h-6 overflow-hidden"
        >
          <motion.p
            key={tagIndex}
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-sm font-semibold uppercase tracking-widest text-zinc-500"
          >
            {TAGLINES[tagIndex]}
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a href="#newsletter">
            <SkewButton variant="primary" data-testid="hero-notify-btn">
              Notify Me <ChevronRight size={18} />
            </SkewButton>
          </a>
          <a href="#dealers">
            <SkewButton variant="secondary" data-testid="hero-dealer-btn">
              Become a Dealer
            </SkewButton>
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.a
        href="#featured"
        style={{ opacity: fade }}
        className="absolute z-20 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown size={18} className="text-orange-500" />
        </motion.span>
      </motion.a>
    </section>
  );
}
